/**
 * `IThirdPartyAppRegistryClient` — typed client for the substrate's
 * third-party app registry surface.
 *
 * Per ADR-027 §3 admission discipline, third-party apps register
 * with SHT under fiduciary admission standards; SHN PBC operates
 * the commercial and technical relationship under SHT-set
 * standards. Apps that register obtain a registered identity, a
 * tier classification per BMA v4.0 §2.2.4, registered scope and
 * purpose, and admission status.
 *
 * The PHG client uses this surface during the SHT-mediated consent
 * flow per ADR-027 §1 step 3:
 *
 *   "the requesting app's identity (verified registered identity per
 *    §3 admission standards, not app-claimed identity), the
 *    requested data scope rendered in patient-comprehensible
 *    language, the stated purpose verbatim from the initiation
 *    parameters..."
 *
 * The client side does NOT trust the app-claimed identity from the
 * initiation parameters; it fetches the verified registered identity
 * from this registry surface and renders THAT to the patient. This
 * is the structural defense against registered-but-misrepresenting
 * apps per ADR-027 §4 disclosure.
 *
 * SCAFFOLD: typed contract + `MockThirdPartyAppRegistryClient` for
 * tests + dev. Real HTTP wiring against the substrate's app-
 * registry service (DEV2 surface; backend tier-classification work
 * on the unclaimed list) lands at production-deployment scope.
 */
export class AppRegistryClientError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.name = 'AppRegistryClientError';
        this.code = code;
    }
}
// ---------- Mock implementation ----------
export class MockThirdPartyAppRegistryClient {
    registry = new Map();
    nextOutcome = 'success';
    async getRegisteredApp(appIdentity) {
        if (this.nextOutcome === 'service-unavailable') {
            throw new AppRegistryClientError('app-registry unavailable', 'service-unavailable');
        }
        return this.registry.get(appIdentity) ?? null;
    }
    /** Test-double helper: register a synthetic app for tests + dev. */
    seedApp(metadata) {
        this.registry.set(metadata.appIdentity, metadata);
    }
    seedNextOutcome(outcome) {
        this.nextOutcome = outcome;
    }
}
//# sourceMappingURL=third-party-app-registry-client.js.map